// src/utils/exportToExcel.js
//
// Tüm liste ekranları için ORTAK, kolon-tabanlı Excel export'u (salt-okunur, veri yazmaz).
// Her sayfa kendi kolon tanımını ("columns") verir, bu util tek sayfalık bir .xlsx üretir.
//
// Kullanım:
//   exportToExcel({
//     rows: filteredSales,
//     columns: [
//       { header: 'Tarih',            value: (r) => r.olusturmaTarihi, type: 'datetime', width: 18 },
//       { header: 'Müşteri',          value: (r) => r.musteriAdSoyad,                    width: 24 },
//       { header: 'Toplam Tutar (₺)', value: (r) => r.toplamTutar,     type: 'money',    width: 16 },
//     ],
//     sheetName: 'Satışlar',
//     fileName: 'Satis_Kayitlari',
//     scopeLabel: 'Goruntulenen',
//   });
//
// Bu modül HİÇBİR şekilde Firestore'a yazmaz; yalnızca eldeki diziyi dosyaya döker.

import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

// Firestore Timestamp / Date / ISO-string / "yyyy-MM-dd" gibi farklı tarih
// formatlarını güvenli şekilde okunur string'e çevirir. Hatada boş döner.
export const formatExportDate = (value, withTime = false) => {
  if (!value) return '';
  const pattern = withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy';
  try {
    if (value.toDate && typeof value.toDate === 'function') {
      return format(value.toDate(), pattern, { locale: tr });
    }
    if (value instanceof Date) {
      return format(value, pattern, { locale: tr });
    }
    if (typeof value === 'number') {
      return format(new Date(value), pattern, { locale: tr });
    }
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return format(d, pattern, { locale: tr });
      return value; // Tarihe çevrilemeyen string'i olduğu gibi koru
    }
  } catch (e) {
    // sessizce yut - export hiçbir zaman çökmemeli
  }
  return '';
};

// Para alanlarını 2 ondalığa yuvarlanmış SAYI olarak döndürür (Excel'de toplam/filtre yapılabilsin diye).
export const toMoney = (value) => {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const toNumber = (value) => {
  const n = Number(value);
  return isFinite(n) ? n : 0;
};

// Tek bir kolon tanımına göre ham değeri Excel hücre değerine çevirir.
const formatCell = (col, row) => {
  let raw;
  try {
    raw = typeof col.value === 'function' ? col.value(row) : row?.[col.value];
  } catch (e) {
    raw = '';
  }

  switch (col.type) {
    case 'money':
      return toMoney(raw);
    case 'number':
      return toNumber(raw);
    case 'date':
      return formatExportDate(raw, false);
    case 'datetime':
      return formatExportDate(raw, true);
    default:
      return raw === null || raw === undefined ? '' : raw;
  }
};

/**
 * Bir veri listesini tek sayfalık Excel dosyası olarak indirir.
 *
 * @param {Object}   params
 * @param {Array}    params.rows         - Dışa aktarılacak kayıtlar.
 * @param {Array}    params.columns      - Kolon tanımları: { header, value(fn|key), type?, width? }.
 *                                         type: 'text'(varsayılan) | 'money' | 'number' | 'date' | 'datetime'.
 * @param {string}   [params.sheetName]  - Excel sayfa adı (maks. 31 karakter).
 * @param {string}   [params.fileName]   - Dosya adı kökü (uzantısız).
 * @param {string}   [params.scopeLabel] - Dosya adı eki, örn "Tumu" / "Goruntulenen".
 * @param {string}   [params.emptyText]  - Kayıt yoksa gösterilecek bilgi metni.
 */
export const exportToExcel = ({
  rows = [],
  columns = [],
  sheetName = 'Liste',
  fileName = 'Liste',
  scopeLabel = '',
  emptyText = 'Kayıt bulunamadı',
}) => {
  const wb = XLSX.utils.book_new();

  const dataRows = rows.map((row) => {
    const obj = {};
    columns.forEach((col) => {
      obj[col.header] = formatCell(col, row);
    });
    return obj;
  });

  const ws = XLSX.utils.json_to_sheet(
    dataRows.length > 0 ? dataRows : [{ Bilgi: emptyText }]
  );

  // Kolon genişlikleri (verilmişse)
  if (columns.some((c) => c.width)) {
    ws['!cols'] = columns.map((c) => ({ wch: c.width || 16 }));
  }

  // Excel sayfa adı 31 karakteri geçemez ve bazı karakterleri içeremez.
  const safeSheetName = String(sheetName).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Liste';
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);

  const tarihEki = format(new Date(), 'yyyy-MM-dd', { locale: tr });
  const ekParca = scopeLabel ? `_${scopeLabel}` : '';
  XLSX.writeFile(wb, `${fileName}${ekParca}_${tarihEki}.xlsx`);
};

export default exportToExcel;
